import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import { localDay } from './dashboard';
import { fmtNum } from './format';
import type { PoKind } from './po';
import type { RentalUse } from './rental';
import type { TodayView } from './today';
import type { SiteRef, SupplierRef } from './types';

/**
 * Panoul de comandă: cinci întrebări — ce am cumpărat, ce am de decis, ce
 * comenzi sunt în desfășurare, unde sunt probleme, ce se întâmplă cu banii.
 *
 * Regula de bază: **nicio cifră fără date în spate**. Ce nu se poate calcula
 * corect lipsește sau spune de ce, nu se umple cu o estimare inventată.
 *
 * Serverul trimite fapte (date calendaristice 'AAAA-LL-ZZ', sume); tot ce
 * depinde de „azi” se socotește aici, în browser, cu ziua utilizatorului.
 */

/** O comandă emisă (din cerere sau suplimentare), cum o vede panoul. */
export interface DashOrder {
  id: string;
  number: string;
  kind: PoKind;
  rfqId: string;
  rfqTitle: string;
  category: string;
  site: SiteRef;
  supplier: SupplierRef;
  /** ISO 8601 */
  issuedAt: string;
  /** ISO 8601: când s-a făcut cererea (timpul de la cerere la comandă); lipsă în datele vechi */
  rfqCreatedAt?: string;
  /** 'AAAA-LL-ZZ' din care contează ca „achiziționat” (`orderCostDay`); null = nu se știe */
  costDay: string | null;
  /** 'AAAA-LL-ZZ': data dorită de pe comandă; null = nescrisă */
  deliveryDate: string | null;
  /** măcar o livrare pe aviz */
  delivered: boolean;
  /** ISO 8601: marcată „livrată integral”; null = în desfășurare */
  completedAt: string | null;
  /** valoarea, lei fără TVA (la chirie livrată, pe zilele reale) */
  valueLei: number;
  /** chiria din avize; null = nu e chirie sau n-a fost livrată */
  rental: RentalUse | null;
  /** zilele de chirie cerute în cerere; null = cumpărare */
  rentDaysRequested: number | null;
}

/** O cerere care încă cere ceva: deschisă sau aleasă fără comandă. */
export interface DashRfq {
  id: string;
  title: string;
  category: string;
  site: SiteRef;
  deadline: string;
  neededBy: string | null;
  createdAt: string;
  closed: boolean;
  /** furnizori invitați / cărora le-a plecat mailul / care au răspuns */
  sends: number;
  sent: number;
  offers: number;
  /** furnizorii cererii, în ordinea tabelului; `answered` = are ofertă (avatarele de pe panou) */
  suppliers?: { id: string; name: string; answered: boolean }[];
  /** cea mai mică ofertă completă, pe toată cantitatea */
  best: { supplier: SupplierRef; total: number } | null;
  /** cât e cea mai mică sub următoarea ofertă completă; null fără două oferte complete */
  gap: number | null;
  /** ofertele pe mai puțin decât s-a cerut: „Cofraj planșeu: ofertat 500 din 1.000 mp” */
  partial: { supplier: SupplierRef; text: string }[];
  /** ofertele cu valabilitate cunoscută */
  validity: { supplier: SupplierRef; validUntil: string; total: number | null }[];
  chosen: { supplier: SupplierRef; total: number | null; at: string } | null;
  /** are comanda din cerere emisă */
  ordered: boolean;
  /** comanda e făcută, dar așteaptă aprobarea (încă nu e emisă) */
  orderPending?: boolean;
}

/** Diferența obținută: doar față de următoarea ofertă completă (decizia proprietarului). */
export interface Savings {
  /** suma diferențelor, lei */
  gained: number;
  /** la câte comenzi s-a putut calcula */
  count: number;
  /** comenzile la care ai ales peste cea mai ieftină: câte și cu cât, la un loc */
  aboveCount: number;
  above: number;
  /** suplimentările, lei: fără concurență, deci fără diferență */
  supplementsLei: number;
}

/** Cererile din ultimele `FUNNEL_DAYS` zile, pe etape. */
export interface Funnel {
  created: number;
  sent: number;
  offers: number;
  chosen: number;
  ordered: number;
  delivered: number;
  completed: number;
}

export const FUNNEL_DAYS = 90;

/* ---------------------------------------------------------------- zile */

const diff = (from: string, to: string): number =>
  Math.round((new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()) / 86_400_000);

/** „azi”, „mâine”, „în 3 zile”, „acum 2 zile” — față de `today`. */
export function whenText(day: string, today: string): string {
  const d = diff(today, day);
  if (d === 0) return tr('azi');
  if (d === 1) return tr('mâine');
  if (d === -1) return tr('ieri');
  return d > 0 ? tr('în {0} zile', d) : tr('acum {0} zile', -d);
}

/* ------------------------------------------------------- comenzi active */

/** Comenzile emise și neterminate, cea mai urgentă primele. */
export function ordersInProgress(orders: DashOrder[]): DashOrder[] {
  return orders
    .filter((o) => !o.completedAt)
    .sort(
      (a, b) =>
        (a.deliveryDate ?? '9999').localeCompare(b.deliveryDate ?? '9999') ||
        (a.issuedAt < b.issuedAt ? -1 : 1),
    );
}

export type OrderPhase = 'late' | 'waiting' | 'onsite' | 'delivered';

/** Unde e o comandă în desfășurare: întârziată, așteaptă livrarea, pe șantier (chirie), livrată. */
export function orderPhase(o: DashOrder, today: string): OrderPhase {
  if (!o.delivered) {
    return o.deliveryDate && o.deliveryDate < today ? 'late' : 'waiting';
  }
  return o.rental && !o.rental.to ? 'onsite' : 'delivered';
}

export function orderPhaseText(o: DashOrder, today: string): string {
  switch (orderPhase(o, today)) {
    case 'late':
      return tr('întârziată, trebuia {0}', whenText(o.deliveryDate!, today));
    case 'waiting':
      return o.deliveryDate ? tr('livrare {0}', whenText(o.deliveryDate, today)) : tr('așteaptă livrarea');
    case 'onsite':
      return tr('pe șantier de {0} {1}', o.rental!.days, o.rental!.days === 1 ? tr('zi') : tr('zile'));
    case 'delivered':
      return o.rental?.to ? tr('returnată, de închis') : tr('livrare pe aviz, de închis');
  }
}

/* --------------------------------------------------------- cereri active */

export type RfqPhase = 'draft' | 'waiting' | 'late' | 'decide' | 'order';

/**
 * Starea unei cereri, cu aceleași reguli ca „Azi” (`todayView`), ca o cerere
 * să nu fie „de decis” într-un loc și „așteaptă” în altul.
 */
export function rfqPhase(r: DashRfq, view: Pick<TodayView, 'decide' | 'late'>): RfqPhase {
  if (r.chosen) return 'order';
  if (view.decide.some((d) => d.rfq.id === r.id)) return 'decide';
  if (view.late.some((l) => l.rfq.id === r.id)) return 'late';
  return r.sent ? 'waiting' : 'draft';
}

export const RFQ_PHASE_TEXT: Record<RfqPhase, string> = {
  get draft() {
    return tr('netrimisă');
  },
  get waiting() {
    return tr('așteaptă răspunsuri');
  },
  get late() {
    return tr('termen depășit');
  },
  get decide() {
    return tr('de decis');
  },
  get order() {
    return tr('de comandat');
  },
};

/* ---------------------------------------------------- necesită atenție */

export type AttentionKind =
  | 'partial'
  | 'expired'
  | 'expiring'
  | 'orderLate'
  | 'neededSoon'
  | 'rentOver'
  | 'rfqLate'
  | 'silent'
  | 'ask';

export interface AttentionItem {
  key: string;
  kind: AttentionKind;
  /** 'high' = roșu, se pierd bani sau timp acum; 'mid' = portocaliu, de urmărit */
  level: 'high' | 'mid';
  title: string;
  detail: string;
  rfqId: string;
  orderId?: string;
  /** furnizorul la care se face acțiunea (follow-up, întrebare) */
  sendId?: string;
}

/** Cu câte zile înainte avertizăm că expiră o ofertă sau că e nevoie de material. */
export const EXPIRY_WARN_DAYS = 2;
export const NEEDED_WARN_DAYS = 5;

/**
 * Problemele reale, din date: fiecare are o cauză care se poate verifica și un
 * loc unde se rezolvă. Fără alerte „de umplutură”. Prețul peste istoric nu e
 * aici încă: istoricul se citește lent la multe cereri (jurnalul, 7 octombrie).
 */
export function attention(
  rfqs: DashRfq[],
  orders: DashOrder[],
  view: Pick<TodayView, 'late' | 'silent' | 'ask'>,
  today: string,
): AttentionItem[] {
  const out: AttentionItem[] = [];

  for (const r of rfqs) {
    // ce ține de alegere contează doar cât timp n-ai ales
    if (!r.chosen && !r.closed) {
      for (const p of r.partial) {
        out.push({
          key: `partial:${r.id}:${p.supplier.id}`,
          kind: 'partial',
          level: 'high',
          title: tr('Cantitate insuficientă'),
          detail: `${p.supplier.name} · ${p.text}`,
          rfqId: r.id,
        });
      }
      for (const v of r.validity) {
        const d = diff(today, v.validUntil);
        if (d < 0 || d > EXPIRY_WARN_DAYS) continue;
        const total = v.total != null ? tr(' · {0} lei', fmtNum(Math.round(v.total))) : '';
        out.push({
          key: `valid:${r.id}:${v.supplier.id}`,
          kind: 'expiring',
          level: 'mid',
          title: tr('Ofertă expiră {0}', whenText(v.validUntil, today)),
          detail: `${v.supplier.name} · ${r.title}${total}`,
          rfqId: r.id,
        });
      }
      // expirată: doar dacă e chiar cea mai ieftină, altfel nu schimbă nimic
      const bestExpired = r.validity.find(
        (v) => r.best && v.supplier.id === r.best.supplier.id && v.validUntil < today,
      );
      if (bestExpired) {
        out.push({
          key: `expired:${r.id}`,
          kind: 'expired',
          level: 'high',
          title: tr('Cea mai bună ofertă a expirat'),
          detail: tr('{0} · {1} · confirmă prețul înainte să comanzi', bestExpired.supplier.name, r.title),
          rfqId: r.id,
        });
      }
    }
    if (!r.ordered && r.neededBy) {
      const d = diff(today, r.neededBy);
      if (d <= NEEDED_WARN_DAYS) {
        out.push({
          key: `needed:${r.id}`,
          kind: 'neededSoon',
          level: d <= EXPIRY_WARN_DAYS ? 'high' : 'mid',
          title:
            d < 0
              ? tr('Materialul trebuia deja pe șantier')
              : tr('Necesar pe șantier {0}', whenText(r.neededBy, today)),
          detail: `${r.title} · ${
            r.orderPending
              ? tr('comanda așteaptă aprobare')
              : r.chosen
                ? tr('ales, comanda nu e emisă')
                : tr('furnizorul nu e ales')
          }`,
          rfqId: r.id,
        });
      }
    }
  }

  for (const o of orders) {
    if (o.completedAt) continue;
    if (!o.delivered && o.deliveryDate && o.deliveryDate < today) {
      out.push({
        key: `late:${o.id}`,
        kind: 'orderLate',
        level: 'high',
        title: tr('Livrare întârziată'),
        detail: tr(
          'Comanda {0} · {1} · trebuia {2}',
          o.number,
          o.supplier.name,
          whenText(o.deliveryDate, today),
        ),
        rfqId: o.rfqId,
        orderId: o.id,
      });
    }
    if (o.rental && !o.rental.to && o.rentDaysRequested != null && o.rental.days > o.rentDaysRequested) {
      out.push({
        key: `rent:${o.id}`,
        kind: 'rentOver',
        level: 'mid',
        title: tr('Chiria depășește durata cerută'),
        detail: tr(
          'Comanda {0} · {1} · pe șantier de {2} zile, cerute {3}',
          o.number,
          o.supplier.name,
          o.rental.days,
          o.rentDaysRequested,
        ),
        rfqId: o.rfqId,
        orderId: o.id,
      });
    }
  }

  for (const l of view.late) {
    out.push({
      key: `rfqlate:${l.rfq.id}`,
      kind: 'rfqLate',
      level: 'mid',
      title: tr('Termen de răspuns depășit'),
      detail: tr(
        '{0} · {1} {2} fără ofertă',
        l.rfq.title,
        l.waiting,
        l.waiting === 1 ? 'furnizor' : tr('furnizori'),
      ),
      rfqId: l.rfq.id,
    });
  }
  for (const s of view.silent) {
    out.push({
      key: `silent:${s.send.id}`,
      kind: 'silent',
      level: 'mid',
      title: tr('Fără răspuns de {0} zile', s.days),
      detail: `${s.send.supplier.name} · ${s.rfq.title}`,
      rfqId: s.rfq.id,
      sendId: s.send.id,
    });
  }
  for (const a of view.ask) {
    out.push({
      key: `ask:${a.send.id}`,
      kind: 'ask',
      level: 'mid',
      title: tr('De întrebat înainte să alegi'),
      detail: tr('{0} · {1} · lipsește {2}', a.send.supplier.name, a.rfq.title, a.questions.join(', ')),
      rfqId: a.rfq.id,
      sendId: a.send.id,
    });
  }

  return out.sort((a, b) => (a.level === b.level ? 0 : a.level === 'high' ? -1 : 1));
}

/* ------------------------------------- achiziționat / angajat / planificat */

export interface CommitMonth {
  /** 'AAAA-LL' */
  key: string;
  label: string;
  name: string;
  /** luna e după luna curentă */
  future: boolean;
  bought: number;
  committed: number;
  planned: number;
}

export interface CommitSeries {
  months: CommitMonth[];
  /** angajat fără dată de livrare: socotit în luna curentă */
  committedUndated: number;
  /** planificat fără „necesar la”: socotit în luna curentă */
  plannedUndated: number;
  /** cereri deschise fără nicio ofertă completă: nu se pot estima */
  plannedUnknown: number;
}

/**
 * Trei serii pe luni:
 * - **Achiziționat**: comenzile emise, în luna livrării, până azi.
 * - **Angajat**: comenzi emise care n-au ajuns încă (livrare viitoare sau fără
 *   dată) — banii sunt promiși, dar nu cheltuiți.
 * - **Planificat**: alegeri fără comandă (valoarea trecută la alegere) și
 *   cereri deschise cu ofertă completă (cea mai mică), în luna „necesar la”.
 *
 * Ce n-are lună se pune în luna curentă și se numără separat, ca tooltipul să
 * spună cât din cifră e fără dată.
 */
export function commitments(
  orders: DashOrder[],
  rfqs: DashRfq[],
  now: number,
  past = 5,
  future = 2,
): CommitSeries {
  const today = localDay(now);
  const azi = new Date(now);
  const months: CommitMonth[] = [];
  for (let i = -past; i <= future; i++) {
    const d = new Date(azi.getFullYear(), azi.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString(intlLocale(), { month: 'short' }),
      name: d.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' }),
      future: i > 0,
      bought: 0,
      committed: 0,
      planned: 0,
    });
  }
  const byKey = new Map(months.map((m) => [m.key, m]));
  const current = byKey.get(today.slice(0, 7))!;
  const at = (day: string | null) => (day ? byKey.get(day.slice(0, 7)) : current);

  let committedUndated = 0;
  let plannedUndated = 0;
  let plannedUnknown = 0;

  for (const o of orders) {
    if (o.costDay && o.costDay <= today) {
      const m = at(o.costDay);
      if (m) m.bought += o.valueLei;
    } else if (!o.completedAt) {
      if (!o.costDay) committedUndated += o.valueLei;
      const m = at(o.costDay);
      if (m) m.committed += o.valueLei;
    }
  }
  for (const r of rfqs) {
    if (r.ordered) continue;
    const value = r.chosen
      ? (r.chosen.total ?? r.best?.total ?? null)
      : r.closed
        ? null
        : (r.best?.total ?? null);
    if (value == null) {
      if (!r.closed || r.chosen) plannedUnknown += 1;
      continue;
    }
    // ce trebuia deja pe șantier și n-are comandă rămâne în luna curentă
    const day = r.neededBy && r.neededBy > today ? r.neededBy : null;
    if (!r.neededBy) plannedUndated += value;
    const m = at(day);
    if (m) m.planned += value;
  }
  return { months, committedUndated, plannedUndated, plannedUnknown };
}

/* --------------------------------------------------- șantiere, categorii */

export interface SiteMoney {
  site: SiteRef;
  bought: number;
  committed: number;
  /** null = bugetul nu e trecut */
  budget: number | null;
}

/** Pe șantier: achiziționat până azi, angajat (emis, neajuns), bugetul dacă există. */
export function moneyBySite(
  orders: DashOrder[],
  budgets: Record<string, number | null>,
  today: string,
): SiteMoney[] {
  const map = new Map<string, SiteMoney>();
  for (const o of orders) {
    const row = map.get(o.site.id) ?? {
      site: o.site,
      bought: 0,
      committed: 0,
      budget: budgets[o.site.id] ?? null,
    };
    if (o.costDay && o.costDay <= today) row.bought += o.valueLei;
    else if (!o.completedAt) row.committed += o.valueLei;
    map.set(o.site.id, row);
  }
  return [...map.values()].sort(
    (a, b) =>
      b.bought + b.committed - (a.bought + a.committed) || a.site.name.localeCompare(b.site.name, 'ro'),
  );
}

export interface CategoryMoney {
  category: string;
  total: number;
  orders: number;
}

/** Pe categorii: tot ce s-a comandat (achiziționat și angajat), după categoria cererii. */
export function moneyByCategory(orders: DashOrder[]): CategoryMoney[] {
  const map = new Map<string, CategoryMoney>();
  for (const o of orders) {
    const row = map.get(o.category) ?? { category: o.category, total: 0, orders: 0 };
    row.total += o.valueLei;
    row.orders += 1;
    map.set(o.category, row);
  }
  return [...map.values()].sort((a, b) => b.total - a.total || a.category.localeCompare(b.category, 'ro'));
}
